'use client';

import { useState, useMemo, useEffect } from 'react';
import SplxListBlock from '@commons/SplxListBlock/SplxListBlock';
import Heading from '@commons/Heading/Heading';
import Dropdown from '@commons/Dropdown/Dropdown';
import CustomCheckbox from '@components/CustomCheckbox/CustomCheckbox';
import './_events-archive-filter.scss';
import { useDesitnationCollection } from '@commons/DesitnationCollection/useDesitnationCollection';
import HtmlToJsx from '@commons/HtmlToJsx/HtmlToJsx';
import { Calendar } from '@icons';
import mapAreasData from '@components/MapApp/areas.json';
import { normalize, cityOf } from '@services/content-filters';

/**
 * Whether an event's address places it in the named area, used when the event
 * has no lat/long (see below) — the same address-text approach the AI event
 * finder uses to resolve places (@services/content-filters.js).
 * @param {{address?: string}} post @param {string} areaName @returns {boolean}
 */
function addressMatchesArea(post, areaName) {
  const wanted = normalize(areaName);
  if (!wanted) return false;
  const city = normalize(cityOf(post));
  if (city === wanted || city.startsWith(wanted)) return true;
  return normalize(post?.address).includes(wanted);
}

/** @param {string} dateStr @param {number} n @returns {string} */
function addDays(dateStr, n) {
  const d = new Date(dateStr + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** @param {string} dateStr @returns {number} Mon=1 … Sun=7 */
function isoWeekday(dateStr) {
  const dow = new Date(dateStr + 'T12:00:00Z').getUTCDay();
  return dow === 0 ? 7 : dow;
}

/** @param {Array<{start:string,end:string,weekdays:number[]}>} openings @param {string} fromDate @returns {string|null} */
function findFirstOccurrence(openings, fromDate) {
  for (const opening of openings) {
    if (opening.end < fromDate) continue;
    const start = opening.start > fromDate ? opening.start : fromDate;
    if (!opening.weekdays.length) return start;
    let d = start;
    for (let i = 0; i < 7; i++, d = addDays(d, 1)) {
      if (d > opening.end) break;
      if (opening.weekdays.includes(isoWeekday(d))) return d;
    }
  }
  return null;
}

/** @param {Array<{start:string,end:string,weekdays:number[]}>} openings @param {string} toDate @returns {string|null} */
function findLastOccurrence(openings, toDate) {
  for (let i = openings.length - 1; i >= 0; i--) {
    const opening = openings[i];
    if (opening.start > toDate) continue;
    const end = opening.end < toDate ? opening.end : toDate;
    if (!opening.weekdays.length) return end;
    let d = end;
    for (let j = 0; j < 7; j++, d = addDays(d, -1)) {
      if (d < opening.start) break;
      if (opening.weekdays.includes(isoWeekday(d))) return d;
    }
  }
  return null;
}

/** @param {Array<{start:string,end:string,weekdays:number[]}>} openings @param {string} todayStr @returns {boolean} */
function isOnlyToday(openings, todayStr) {
  return (
    findFirstOccurrence(openings, todayStr) === todayStr && findFirstOccurrence(openings, addDays(todayStr, 1)) === null
  );
}

function FilterDropdown({ id, value, onChange, options, placeholder, multiselect = true }) {
  let displayLabel = placeholder;

  if (value.length === 1) {
    displayLabel = options.find((o) => o.value === value[0])?.label || placeholder;
  } else if (value.length > 1) {
    displayLabel = `${placeholder} (${value.length})`;
  }

  function handleSelect(val) {
    if (multiselect) {
      if (value.includes(val)) {
        onChange(value.filter((v) => v !== val));
      } else {
        onChange([...value, val]);
      }
    } else {
      onChange([val]);
    }
  }

  const longestOptionLabel = options.reduce((longest, current) => {
    return current.label.length > longest.length ? current.label : longest;
  }, placeholder);

  return (
    <Dropdown
      id={id}
      label={displayLabel}
      longestOptionLabel={longestOptionLabel}
      multiselect={multiselect}
      className="EventsArchiveFilter__dropdown-inner"
    >
      <ul className="EventsArchiveFilter__option-list">
        {options.map((opt) => {
          const isSelected = value.includes(opt.value);
          return (
            <li key={opt.value} className={opt.isRoot ? 'EventsArchiveFilter__root-option' : ''}>
              {multiselect ? (
                <CustomCheckbox
                  name={`filter-${id}`}
                  checked={isSelected}
                  onChange={() => handleSelect(opt.value)}
                  className={opt.isRoot ? 'EventsArchiveFilter__checkbox--root' : ''}
                >
                  <HtmlToJsx html={opt.label} />
                </CustomCheckbox>
              ) : (
                <button
                  type="button"
                  className={`EventsArchiveFilter__single-option ${isSelected ? 'is-selected' : ''}`}
                  onClick={() => handleSelect(opt.value)}
                >
                  <HtmlToJsx html={opt.label} />
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </Dropdown>
  );
}

export default function EventsArchiveFilter({
  posts = [],
  heading = '',
  headingLevel = 2,
  dictionary,
  limit = null,
  ...splxProps
}) {
  const [selectedSorts, setSelectedSorts] = useState([]);
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // Polyfill logic has been moved to Dropdown component

  const mapAreas = useMemo(() => {
    const flatAreas = [];
    // Skip the generic whole-county root
    const rootAreas = mapAreasData.filter((a) => a.key !== 'wholeTelemark');

    rootAreas.forEach((parent) => {
      const plabel = dictionary?.map?.areas?.[parent.key] || parent.name;

      flatAreas.push({
        id: parent.name,
        label: plabel,
        lat: parent.coordinates[1],
        long: parent.coordinates[0],
        parentId: null
      });

      if (parent.children) {
        parent.children.forEach((child) => {
          flatAreas.push({
            id: child.name,
            label: child.name,
            lat: child.coordinates[1],
            long: child.coordinates[0],
            parentId: parent.name
          });
        });
      }
    });
    return flatAreas;
  }, [dictionary]);

  const uniqueAreas = useMemo(() => {
    return mapAreas.map((area) => ({
      value: area.id,
      // Add indentation for child areas visually
      label: area.parentId ? `\u00A0\u00A0\u00A0\u00A0${area.label}` : area.label,
      isRoot: !area.parentId
    }));
  }, [mapAreas]);

  const { categories, tags } = useDesitnationCollection();

  const uniqueTypes = useMemo(() => {
    const typeMap = new Map();
    posts.forEach((post) => {
      if (post.categories?.length && categories?.length) {
        post.categories.forEach((c) => {
          const match = categories.find((cat) => cat.id === c);
          if (match?.title) typeMap.set(c, match.title);
        });
      }
      if (post.tags?.length && tags?.length) {
        post.tags.forEach((t) => {
          const match = tags.find((tag) => tag.id === t);
          if (match?.title) typeMap.set(t, match.title);
        });
      }
    });
    return Array.from(typeMap.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([id, title]) => ({ value: id, label: title }));
  }, [posts, categories, tags]);

  const filteredPosts = useMemo(() => {
    let result = [...posts];

    if (selectedAreas.length || selectedTypes.length) {
      result = result.filter((post) => {
        let matchesArea = false;
        let matchesType = false;

        if (selectedAreas.length) {
          const pLat = parseFloat(post.lat);
          const pLong = parseFloat(post.long);

          if (!isNaN(pLat) && !isNaN(pLong)) {
            let closestAreaId = null;
            let closestParentId = null;
            let minDistanceSq = Infinity;

            for (const area of mapAreas) {
              const dLat = pLat - area.lat;
              const dLong = pLong - area.long;
              const distSq = dLat * dLat + dLong * dLong;

              if (distSq < minDistanceSq) {
                minDistanceSq = distSq;
                closestAreaId = area.id;
                closestParentId = area.parentId;
              }
            }

            // Matches if the exact closest region is selected, OR its parent region is selected
            matchesArea =
              selectedAreas.includes(closestAreaId) || (closestParentId && selectedAreas.includes(closestParentId));
          } else {
            // No coordinates on this event (e.g. missing from the DMS feed) — fall
            // back to matching the area name against the event's address text.
            matchesArea = mapAreas.some(
              (area) =>
                (selectedAreas.includes(area.id) || (area.parentId && selectedAreas.includes(area.parentId))) &&
                addressMatchesArea(post, area.id)
            );
          }
        }

        if (selectedTypes.length) {
          if (post.categories?.some((c) => selectedTypes.includes(c))) matchesType = true;
          if (post.tags?.some((t) => selectedTypes.includes(t))) matchesType = true;
        }

        if (selectedAreas.length && selectedTypes.length) return matchesArea && matchesType;
        if (selectedAreas.length) return matchesArea;
        if (selectedTypes.length) return matchesType;

        return true;
      });
    }

    const todayStr = new Date().toISOString().slice(0, 10);

    if (fromDate && !toDate) {
      // Single-date filter: only events with an occurrence on exactly fromDate.
      result = result.filter((post) => {
        const openings = post.openings;
        if (openings?.length) return findFirstOccurrence(openings, fromDate) === fromDate;
        // Fallback when openings not yet available: fromDate falls within overall range.
        if (!post.start_date) return false;
        return post.start_date <= fromDate && (!post.end_date || post.end_date >= fromDate);
      });
      result = result.map((post) => ({ ...post, display_date: fromDate, display_end_date: null }));
    } else if (fromDate && toDate) {
      // Range filter: event must have at least one occurrence within [fromDate, toDate].
      result = result.filter((post) => {
        const openings = post.openings;
        if (openings?.length) {
          const first = findFirstOccurrence(openings, fromDate);
          return first !== null && first <= toDate;
        }
        // Fallback: overall date range overlaps with filter window.
        if (!post.start_date) return true;
        return post.start_date <= toDate && (!post.end_date || post.end_date >= fromDate);
      });
      result = result.map((post) => {
        if (!post.start_date) return post;
        const openings = post.openings || [];
        const displayDate =
          findFirstOccurrence(openings, fromDate) || (fromDate > post.start_date ? fromDate : post.start_date);
        const last =
          findLastOccurrence(openings, toDate) ||
          (toDate < (post.end_date || toDate) ? toDate : post.end_date || toDate);
        const displayEndDate = last && last > displayDate ? last : null;
        return { ...post, display_date: displayDate, display_end_date: displayEndDate };
      });
    } else {
      // No date filter: events occurring today (per openings schedule) show only today's date.
      result = result.map((post) => {
        const openings = post.openings;
        const isToday = openings?.length
          ? findFirstOccurrence(openings, todayStr) === todayStr
          : post.date === todayStr;
        if (!isToday) return post;
        return { ...post, display_date: todayStr, display_end_date: null };
      });
    }

    if (selectedSorts.includes('az')) {
      result.sort((a, b) => {
        const hasDateA = !!a.date;
        const hasDateB = !!b.date;
        if (hasDateA !== hasDateB) return hasDateA ? -1 : 1;
        return (a.title || '').localeCompare(b.title || '');
      });
    } else if (selectedSorts.includes('za')) {
      result.sort((a, b) => {
        const hasDateA = !!a.date;
        const hasDateB = !!b.date;
        if (hasDateA !== hasDateB) return hasDateA ? -1 : 1;
        return (b.title || '').localeCompare(a.title || '');
      });
    } else {
      // Sort by display_date (the date shown in Blurb.Date) — the first relevant occurrence.
      // Falls back to post.date (PHP-computed next occurrence) when no display_date is set.
      // Tiebreaker (no date filter): today-only events sort above multi-day events.
      result.sort((a, b) => {
        const dateA = a.display_date || a.date || a.start_date;
        const dateB = b.display_date || b.date || b.start_date;
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        if (dateA !== dateB) return dateA < dateB ? -1 : 1;
        if (!fromDate) {
          const aOnlyToday = isOnlyToday(a.openings || [], todayStr);
          const bOnlyToday = isOnlyToday(b.openings || [], todayStr);
          if (aOnlyToday !== bOnlyToday) return aOnlyToday ? -1 : 1;
        }
        return 0;
      });
    }

    return result;
  }, [posts, selectedAreas, selectedTypes, selectedSorts, fromDate, toDate, mapAreas]);

  const sortOptions = [
    { value: 'oldest', label: dictionary?.archive?.upcoming || 'Kommende' },
    { value: 'za', label: dictionary?.archive?.za || 'Å - A' },
    { value: 'az', label: dictionary?.archive?.az || 'A - Å' }
  ];

  return (
    <div className="EventsArchiveFilter Layout--subgrid">
      {heading && (
        <Heading className="SplxListBlock__heading" level={headingLevel} visualLevel={4}>
          {heading}
        </Heading>
      )}

      <div className="EventsArchiveFilter__controls">
        <div className="EventsArchiveFilter__group EventsArchiveFilter__group--filter">
          <span className="EventsArchiveFilter__label">{dictionary?.archive?.filter || 'Filter'}</span>

          <div className="EventsArchiveFilter__date-picker">
            <input
              type="date"
              value={fromDate}
              max={toDate}
              onChange={(e) => setFromDate(e.target.value)}
              onClick={(e) => {
                if ('showPicker' in e.target) {
                  // @ts-ignore
                  try {
                    e.target.showPicker();
                  } catch (err) {}
                }
              }}
              title={dictionary?.archive?.fromDate || 'Fra'}
              className="is-transparent"
            />
            {!fromDate ? (
              <span className="EventsArchiveFilter__date-placeholder">{dictionary?.archive?.fromDate || 'Fra'}</span>
            ) : (
              <span className="EventsArchiveFilter__date-display">
                {new Date(fromDate).toLocaleDateString('no-NO', { day: '2-digit', month: '2-digit', year: 'numeric' })}
              </span>
            )}
            <Calendar />
          </div>
          <div className="EventsArchiveFilter__date-picker">
            <input
              type="date"
              value={toDate}
              min={fromDate}
              onChange={(e) => setToDate(e.target.value)}
              onClick={(e) => {
                if ('showPicker' in e.target) {
                  // @ts-ignore
                  try {
                    e.target.showPicker();
                  } catch (err) {}
                }
              }}
              title={dictionary?.archive?.toDate || 'Til'}
              className="is-transparent"
            />
            {!toDate ? (
              <span className="EventsArchiveFilter__date-placeholder">{dictionary?.archive?.toDate || 'Til'}</span>
            ) : (
              <span className="EventsArchiveFilter__date-display">
                {new Date(toDate).toLocaleDateString('no-NO', { day: '2-digit', month: '2-digit', year: 'numeric' })}
              </span>
            )}
            <Calendar />
          </div>

          {uniqueAreas.length > 0 && (
            <FilterDropdown
              id="area"
              value={selectedAreas}
              onChange={setSelectedAreas}
              options={uniqueAreas}
              placeholder={dictionary?.archive?.area || 'Område'}
            />
          )}

          {uniqueTypes.length > 0 && (
            <FilterDropdown
              id="type"
              value={selectedTypes}
              onChange={setSelectedTypes}
              options={uniqueTypes}
              placeholder={dictionary?.archive?.type || 'Type'}
            />
          )}
        </div>

        <div className="EventsArchiveFilter__group EventsArchiveFilter__group--sort">
          <span className="EventsArchiveFilter__label">{dictionary?.archive?.sort || 'Sortering'}</span>

          <FilterDropdown
            id="sort"
            value={selectedSorts}
            onChange={setSelectedSorts}
            options={sortOptions}
            placeholder={dictionary?.archive?.upcoming || 'Nyeste først'}
            multiselect={false}
          />
        </div>
      </div>

      <SplxListBlock
        {...splxProps}
        headingLevel={heading ? headingLevel + 1 : headingLevel}
        posts={filteredPosts}
        posts_per_page={20}
        preFiltered
      />
    </div>
  );
}
